import React, { useState } from "react";
import {
  StyleSheet,
  TouchableOpacity,
  Platform,
  StyleProp,
  ViewStyle,
  TextStyle,
} from "react-native";
import { router } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "./ThemedText";
import { EntityIcon } from "./ui/EntityIcon";
import { EntityType, normalizeEntityType } from "@/constants/entity-icons";
import { useThemeColor } from "@/hooks/useThemeColor";

/**
 * Resolves the canonical Expo Router navigation route for a given entity type and ID.
 *
 * @param {EntityType | string} type - The entity type (memory, media, contact, group, share, message).
 * @param {string} id - The entity unique ID.
 * @returns {string} The resolved router path.
 */
export function getEntityRoute(type: EntityType | string, id: string): string {
  const normType = normalizeEntityType(type);
  const cleanId = encodeURIComponent((id || "").trim());

  switch (normType) {
    case "memory":
      return `/(tabs)/my-memories/${cleanId}`;
    case "media":
      return `/(tabs)/my-media/${cleanId}`;
    case "contact":
      return `/(tabs)/my-contacts/${cleanId}`;
    case "group":
      return `/(tabs)/my-groups/${cleanId}`;
    case "share":
      return `/(tabs)/my-shares/${cleanId}`;
    case "message":
      return `/(tabs)/messages/${cleanId}`;
    case "place":
      return `/(tabs)/my-places/${cleanId}`;
    case "anchor":
      return `/(tabs)/my-anchors/${cleanId}`;
    default:
      return `/(tabs)/my-memories/${cleanId}`;
  }
}

/**
 * Visual presentation variants for ThemedEntityLink.
 */
export type EntityLinkVariant = "inline" | "title" | "badge" | "subtle";

/**
 * Props for the ThemedEntityLink component.
 */
export interface IThemedEntityLinkProps {
  /** The target domain entity type */
  type: EntityType | string;
  /** Unique entity ID to navigate to */
  id: string;
  /** Primary display name or title of the referenced entity */
  displayName?: string;
  /** Visual presentation variant (defaults to "inline") */
  variant?: EntityLinkVariant;
  /** Whether to prepend the standardized EntityIcon */
  showIcon?: boolean;
  /** Custom icon size (defaults to 14 for inline, 18 for title) */
  iconSize?: number;
  /** Whether to append a subtle external link indicator */
  showOpenIcon?: boolean;
  /** Text truncation limit */
  numberOfLines?: number;
  /** Outer container style */
  style?: StyleProp<ViewStyle>;
  /** Text styling override */
  textStyle?: StyleProp<TextStyle>;
  /** Accessibility label for assistive technology */
  accessibilityLabel?: string;
  /** Optional custom navigation handler (overrides router.push) */
  onPress?: () => void;
  /** Optional custom child content to render inside the link touchable */
  children?: React.ReactNode;
  /** Optional testID for automation */
  testID?: string;
}

/**
 * ThemedEntityLink: Universal interactive entity linking component.
 *
 * Provides a consistent, accessible tap-to-navigate affordance for any referenced
 * Memory, Media, Contact, Group, Share, or Message across the entire Memories platform.
 * Adheres strictly to the rule: "Do not have a separate button - the text should have the link."
 *
 * @param {IThemedEntityLinkProps} props - Component properties.
 * @returns {React.JSX.Element} Rendered clickable entity link.
 */
export function ThemedEntityLink({
  type,
  id,
  displayName,
  variant = "inline",
  showIcon = false,
  iconSize,
  showOpenIcon = false,
  numberOfLines = 1,
  style,
  textStyle,
  accessibilityLabel,
  onPress,
  children,
  testID,
}: IThemedEntityLinkProps): React.JSX.Element {
  const [isHovered, setIsHovered] = useState(false);

  const tintColor = useThemeColor({}, "tint");
  const textColor = useThemeColor({}, "text");
  const borderColor = useThemeColor({}, "border");
  const badgeBg = useThemeColor({}, "buttonSecondary");

  const normalizedType = normalizeEntityType(type);
  const resolvedLabel = displayName?.trim() || id || `${normalizedType} item`;

  const handlePress = () => {
    if (onPress) {
      onPress();
      return;
    }
    if (!id || id === "unknown") return;

    const route = getEntityRoute(normalizedType, id);
    try {
      router.push(route as any);
    } catch (err) {
      console.warn(`[ThemedEntityLink] Navigation failed for route "${route}":`, err);
    }
  };

  const a11yLabel =
    accessibilityLabel ||
    `Open ${normalizedType} ${resolvedLabel} in a new screen`;

  // Render badge variant
  if (variant === "badge") {
    return (
      <TouchableOpacity
        activeOpacity={0.7}
        onPress={handlePress}
        accessibilityRole="link"
        accessibilityLabel={a11yLabel}
        testID={testID}
        style={[
          styles.badgeContainer,
          {
            borderColor: isHovered ? tintColor : borderColor,
            backgroundColor: isHovered ? "rgba(74, 144, 226, 0.15)" : badgeBg,
          },
          style,
        ]}
        {...(Platform.OS === "web"
          ? {
              onMouseEnter: () => setIsHovered(true),
              onMouseLeave: () => setIsHovered(false),
            }
          : {})}
      >
        <EntityIcon
          type={normalizedType}
          size={iconSize || 13}
          color={tintColor}
          style={styles.iconMargin}
        />
        <ThemedText
          style={[
            styles.badgeText,
            { color: isHovered ? tintColor : textColor },
            textStyle,
          ]}
          numberOfLines={numberOfLines}
        >
          {resolvedLabel}
        </ThemedText>
        {showOpenIcon && (
          <Ionicons
            name="open-outline"
            size={11}
            color={tintColor}
            style={styles.openIconMargin}
          />
        )}
      </TouchableOpacity>
    );
  }

  // Render title variant
  if (variant === "title") {
    return (
      <TouchableOpacity
        activeOpacity={0.7}
        onPress={handlePress}
        accessibilityRole="link"
        accessibilityLabel={a11yLabel}
        testID={testID}
        style={[styles.titleContainer, style]}
        {...(Platform.OS === "web"
          ? {
              onMouseEnter: () => setIsHovered(true),
              onMouseLeave: () => setIsHovered(false),
            }
          : {})}
      >
        {showIcon && (
          <EntityIcon
            type={normalizedType}
            size={iconSize || 18}
            color={tintColor}
            style={styles.iconMargin}
          />
        )}
        {children || (
          <ThemedText
            type="editorialTitle"
            style={[
              styles.titleText,
              {
                color: isHovered ? tintColor : textColor,
                textDecorationLine: isHovered ? "underline" : "none",
              },
              textStyle,
            ]}
            numberOfLines={numberOfLines}
          >
            {resolvedLabel}
          </ThemedText>
        )}
        {showOpenIcon && (
          <Ionicons
            name="open-outline"
            size={14}
            color={tintColor}
            style={styles.openIconMargin}
          />
        )}
      </TouchableOpacity>
    );
  }

  // Render subtle or inline variant (default)
  const isSubtle = variant === "subtle";
  const defaultColor = isSubtle ? textColor : tintColor;
  const activeColor = isHovered ? tintColor : defaultColor;

  return (
    <TouchableOpacity
      activeOpacity={0.7}
      onPress={handlePress}
      accessibilityRole="link"
      accessibilityLabel={a11yLabel}
      testID={testID}
      style={[
        styles.inlineContainer,
        children ? styles.childrenContainer : null,
        style,
      ]}
      {...(Platform.OS === "web"
        ? {
            onMouseEnter: () => setIsHovered(true),
            onMouseLeave: () => setIsHovered(false),
          }
        : {})}
    >
      {showIcon && (
        <EntityIcon
          type={normalizedType}
          size={iconSize || 14}
          color={activeColor}
          style={styles.iconMargin}
        />
      )}
      {children || (
        <ThemedText
          style={[
            styles.inlineText,
            {
              color: activeColor,
              textDecorationLine: isHovered || !isSubtle ? "underline" : "none",
            },
            textStyle,
          ]}
          numberOfLines={numberOfLines}
        >
          {resolvedLabel}
        </ThemedText>
      )}
      {showOpenIcon && (
        <Ionicons
          name="open-outline"
          size={12}
          color={activeColor}
          style={styles.openIconMargin}
        />
      )}
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  inlineContainer: {
    flexDirection: "row",
    alignItems: "center",
    display: "inline-flex" as any,
    cursor: "pointer" as any,
  },
  childrenContainer: {
    display: "flex" as any,
  },
  inlineText: {
    fontSize: 14,
    fontWeight: "600",
  },
  titleContainer: {
    flexDirection: "row",
    alignItems: "center",
    cursor: "pointer" as any,
  },
  titleText: {
    fontSize: 20,
    fontWeight: "700",
  },
  badgeContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 0,
    borderWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 4,
    cursor: "pointer" as any,
  },
  badgeText: {
    fontSize: 12,
    fontWeight: "600",
  },
  iconMargin: {
    marginRight: 5,
  },
  openIconMargin: {
    marginLeft: 4,
  },
});

export default ThemedEntityLink;
